import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  TrendingUp, 
  TrendingDown, 
  Wallet,
  PlusCircle,
  Edit2,
  Trash2,
  Calendar,
  CreditCard,
  Building2,
  FolderKanban,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  FileText,
  Search,
  Filter,
  BarChart3,
  Euro,
  Download,
  FileSpreadsheet,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function RendicontazioneEconomica() {
  // State
  const [activeTab, setActiveTab] = useState('riepilogo');
  const [loading, setLoading] = useState(true);
  
  // Data
  const [riepilogo, setRiepilogo] = useState(null);
  const [entrate, setEntrate] = useState([]);
  const [uscite, setUscite] = useState([]);
  const [categorieEntrate, setCategorieEntrate] = useState([]);
  const [categorieUscite, setCategorieUscite] = useState([]);
  const [progetti, setProgetti] = useState([]);
  const [report, setReport] = useState(null);
  
  // Filters
  const [annoFiltro, setAnnoFiltro] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState('');
  
  // Export states
  const [exporting, setExporting] = useState(false);
  const [exportProgettoId, setExportProgettoId] = useState('');
  
  // Dialog states
  const [showEntrataDialog, setShowEntrataDialog] = useState(false);
  const [showUscitaDialog, setShowUscitaDialog] = useState(false);
  const [showProgettoDialog, setShowProgettoDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    data_operazione: new Date().toISOString().split('T')[0],
    importo: '',
    descrizione: '',
    categoria_id: '',
    modalita_incasso: 'banca',
    modalita_pagamento: 'bonifico',
    progetto_id: '',
    note: ''
  });
  
  const [progettoForm, setProgettoForm] = useState({
    nome: '',
    descrizione: '',
    data_inizio: '',
    data_fine: '',
    budget_previsto: '',
    stato: 'attivo'
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'report') {
      loadReport();
    }
  }, [activeTab, annoFiltro]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [riepilogoRes, entrateRes, usciteRes, catEntrateRes, catUsciteRes, progettiRes] = await Promise.all([
        axios.get(`${API}/api/rendicontazione/riepilogo`, { withCredentials: true }),
        axios.get(`${API}/api/rendicontazione/entrate`, { withCredentials: true }),
        axios.get(`${API}/api/rendicontazione/uscite`, { withCredentials: true }),
        axios.get(`${API}/api/rendicontazione/categorie-entrate`, { withCredentials: true }),
        axios.get(`${API}/api/rendicontazione/categorie-uscite`, { withCredentials: true }),
        axios.get(`${API}/api/rendicontazione/progetti`, { withCredentials: true })
      ]);
      
      setRiepilogo(riepilogoRes.data);
      setEntrate(entrateRes.data);
      setUscite(usciteRes.data);
      setCategorieEntrate(catEntrateRes.data);
      setCategorieUscite(catUsciteRes.data);
      setProgetti(progettiRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const loadReport = async () => {
    try {
      const res = await axios.get(`${API}/api/rendicontazione/report?anno=${annoFiltro}`, { withCredentials: true });
      setReport(res.data);
    } catch (error) {
      console.error('Error loading report:', error);
    }
  };

  // CRUD handlers
  const handleSaveEntrata = async () => {
    try {
      const data = {
        ...formData,
        importo: parseFloat(formData.importo),
        progetto_id: formData.progetto_id || null
      };
      
      if (editingItem) {
        await axios.put(`${API}/api/rendicontazione/entrate/${editingItem.entrata_id}`, data, { withCredentials: true });
        toast.success('Entrata aggiornata');
      } else {
        await axios.post(`${API}/api/rendicontazione/entrate`, data, { withCredentials: true });
        toast.success('Entrata registrata');
      }
      
      setShowEntrataDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const handleSaveUscita = async () => {
    try {
      const data = {
        ...formData,
        importo: parseFloat(formData.importo),
        progetto_id: formData.progetto_id || null
      };
      
      if (editingItem) {
        await axios.put(`${API}/api/rendicontazione/uscite/${editingItem.uscita_id}`, data, { withCredentials: true });
        toast.success('Uscita aggiornata');
      } else {
        await axios.post(`${API}/api/rendicontazione/uscite`, data, { withCredentials: true });
        toast.success('Uscita registrata');
      }
      
      setShowUscitaDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const handleSaveProgetto = async () => {
    try {
      const data = {
        ...progettoForm,
        budget_previsto: progettoForm.budget_previsto ? parseFloat(progettoForm.budget_previsto) : null
      };
      
      if (editingItem) {
        await axios.put(`${API}/api/rendicontazione/progetti/${editingItem.progetto_id}`, data, { withCredentials: true });
        toast.success('Progetto aggiornato');
      } else {
        await axios.post(`${API}/api/rendicontazione/progetti`, data, { withCredentials: true });
        toast.success('Progetto creato');
      }
      
      setShowProgettoDialog(false);
      setProgettoForm({ nome: '', descrizione: '', data_inizio: '', data_fine: '', budget_previsto: '', stato: 'attivo' });
      setEditingItem(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const handleDeleteEntrata = async (id) => {
    if (!confirm('Eliminare questa entrata?')) return;
    try {
      await axios.delete(`${API}/api/rendicontazione/entrate/${id}`, { withCredentials: true });
      toast.success('Entrata eliminata');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella cancellazione');
    }
  };

  const handleDeleteUscita = async (id) => {
    if (!confirm('Eliminare questa uscita?')) return;
    try {
      await axios.delete(`${API}/api/rendicontazione/uscite/${id}`, { withCredentials: true });
      toast.success('Uscita eliminata');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella cancellazione');
    }
  };

  const handleDeleteProgetto = async (id) => {
    if (!confirm('Eliminare questo progetto?')) return;
    try {
      await axios.delete(`${API}/api/rendicontazione/progetti/${id}`, { withCredentials: true });
      toast.success('Progetto eliminato');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella cancellazione');
    }
  };

  const resetForm = () => {
    setFormData({
      data_operazione: new Date().toISOString().split('T')[0],
      importo: '',
      descrizione: '',
      categoria_id: '',
      modalita_incasso: 'banca',
      modalita_pagamento: 'bonifico',
      progetto_id: '',
      note: ''
    });
    setEditingItem(null);
  };

  const openEditEntrata = (item) => {
    setEditingItem(item);
    setFormData({
      data_operazione: item.data_operazione,
      importo: item.importo.toString(),
      descrizione: item.descrizione,
      categoria_id: item.categoria_id,
      modalita_incasso: item.modalita_incasso,
      progetto_id: item.progetto_id || '',
      note: item.note || ''
    });
    setShowEntrataDialog(true);
  };

  const openEditUscita = (item) => {
    setEditingItem(item);
    setFormData({
      data_operazione: item.data_operazione,
      importo: item.importo.toString(),
      descrizione: item.descrizione,
      categoria_id: item.categoria_id,
      modalita_pagamento: item.modalita_pagamento,
      progetto_id: item.progetto_id || '',
      note: item.note || ''
    });
    setShowUscitaDialog(true);
  };

  const openEditProgetto = (item) => {
    setEditingItem(item);
    setProgettoForm({
      nome: item.nome,
      descrizione: item.descrizione || '',
      data_inizio: item.data_inizio || '',
      data_fine: item.data_fine || '',
      budget_previsto: item.budget_previsto?.toString() || '',
      stato: item.stato
    });
    setShowProgettoDialog(true);
  };

  // Export functions
  const handleExportPDF = async (progettoId = null) => {
    setExporting(true);
    try {
      let url = `${API}/api/rendicontazione/export/pdf?anno=${annoFiltro}`;
      if (progettoId) {
        url += `&progetto_id=${progettoId}`;
      }
      
      const response = await axios.get(url, { 
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = progettoId 
        ? `Bilancio_Progetto_${annoFiltro}.pdf`
        : `Bilancio_${annoFiltro}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
      
      toast.success('PDF generato con successo');
    } catch (error) {
      console.error('Export PDF error:', error);
      toast.error('Errore nella generazione del PDF');
    } finally {
      setExporting(false);
    }
  };

  const handleExportCSV = async (tipo = 'completo', progettoId = null) => {
    setExporting(true);
    try {
      let url = `${API}/api/rendicontazione/export/csv?anno=${annoFiltro}&tipo=${tipo}`;
      if (progettoId) {
        url += `&progetto_id=${progettoId}`;
      }
      
      const response = await axios.get(url, { 
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `Bilancio_${annoFiltro}_${tipo}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
      
      toast.success('CSV generato con successo');
    } catch (error) {
      console.error('Export CSV error:', error);
      toast.error('Errore nella generazione del CSV');
    } finally {
      setExporting(false);
    }
  };

  // Filter functions
  const filteredEntrate = entrate.filter(e => 
    e.descrizione?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.categoria_nome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUscite = uscite.filter(u => 
    u.descrizione?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.categoria_nome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0055BB]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rendicontazione-economica-page">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0055BB]/10 rounded-full mb-4">
          <Euro className="w-8 h-8 text-[#0055BB]" />
        </div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Rendicontazione Economica
        </h1>
        <p className="text-slate-500 mt-2">
          Gestione entrate, uscite e bilancio dell'associazione
        </p>
      </div>

      {/* Summary Cards */}
      {riepilogo && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Entrate Anno</p>
                  <p className="text-2xl font-bold text-green-700">{formatCurrency(riepilogo.entrate_anno)}</p>
                </div>
                <div className="bg-green-200/50 p-3 rounded-full">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600 font-medium">Uscite Anno</p>
                  <p className="text-2xl font-bold text-red-700">{formatCurrency(riepilogo.uscite_anno)}</p>
                </div>
                <div className="bg-red-200/50 p-3 rounded-full">
                  <TrendingDown className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`bg-gradient-to-br ${riepilogo.saldo_anno >= 0 ? 'from-blue-50 to-blue-100 border-blue-200' : 'from-orange-50 to-orange-100 border-orange-200'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${riepilogo.saldo_anno >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Saldo Anno</p>
                  <p className={`text-2xl font-bold ${riepilogo.saldo_anno >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                    {formatCurrency(riepilogo.saldo_anno)}
                  </p>
                </div>
                <div className={`p-3 rounded-full ${riepilogo.saldo_anno >= 0 ? 'bg-blue-200/50' : 'bg-orange-200/50'}`}>
                  <Wallet className={`w-6 h-6 ${riepilogo.saldo_anno >= 0 ? 'text-blue-600' : 'text-orange-600'}`} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium">Saldo Mese</p>
                  <p className="text-2xl font-bold text-slate-700">{formatCurrency(riepilogo.saldo_mese)}</p>
                </div>
                <div className="bg-slate-200/50 p-3 rounded-full">
                  <Calendar className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full max-w-2xl mx-auto">
          <TabsTrigger value="riepilogo">Riepilogo</TabsTrigger>
          <TabsTrigger value="entrate">Entrate</TabsTrigger>
          <TabsTrigger value="uscite">Uscite</TabsTrigger>
          <TabsTrigger value="progetti">Progetti</TabsTrigger>
          <TabsTrigger value="report">Report</TabsTrigger>
        </TabsList>

        {/* RIEPILOGO TAB */}
        <TabsContent value="riepilogo" className="mt-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Azioni Rapide</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  className="w-full justify-start bg-green-600 hover:bg-green-700" 
                  onClick={() => { resetForm(); setShowEntrataDialog(true); }}
                >
                  <ArrowUpRight className="w-4 h-4 mr-2" />
                  Registra Entrata
                </Button>
                <Button 
                  className="w-full justify-start bg-red-600 hover:bg-red-700"
                  onClick={() => { resetForm(); setShowUscitaDialog(true); }}
                >
                  <ArrowDownRight className="w-4 h-4 mr-2" />
                  Registra Uscita
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => { setEditingItem(null); setProgettoForm({ nome: '', descrizione: '', data_inizio: '', data_fine: '', budget_previsto: '', stato: 'attivo' }); setShowProgettoDialog(true); }}
                >
                  <FolderKanban className="w-4 h-4 mr-2" />
                  Nuovo Progetto
                </Button>
              </CardContent>
            </Card>

            {/* Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Statistiche</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-600">Totale Entrate</span>
                    <Badge variant="outline" className="bg-green-50 text-green-700">{riepilogo?.num_entrate || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-600">Totale Uscite</span>
                    <Badge variant="outline" className="bg-red-50 text-red-700">{riepilogo?.num_uscite || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-600">Progetti Attivi</span>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700">{riepilogo?.num_progetti || 0}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Transactions */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Ultime Operazioni</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {[...entrate.slice(0, 3).map(e => ({...e, tipo: 'entrata'})), ...uscite.slice(0, 3).map(u => ({...u, tipo: 'uscita'}))]
                    .sort((a, b) => new Date(b.data_operazione) - new Date(a.data_operazione))
                    .slice(0, 5)
                    .map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          {item.tipo === 'entrata' ? (
                            <div className="bg-green-100 p-2 rounded-full">
                              <ArrowUpRight className="w-4 h-4 text-green-600" />
                            </div>
                          ) : (
                            <div className="bg-red-100 p-2 rounded-full">
                              <ArrowDownRight className="w-4 h-4 text-red-600" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-slate-900">{item.descrizione}</p>
                            <p className="text-xs text-slate-500">{item.data_operazione} • {item.categoria_nome}</p>
                          </div>
                        </div>
                        <span className={`font-semibold ${item.tipo === 'entrata' ? 'text-green-600' : 'text-red-600'}`}>
                          {item.tipo === 'entrata' ? '+' : '-'}{formatCurrency(item.importo)}
                        </span>
                      </div>
                    ))}
                  {entrate.length === 0 && uscite.length === 0 && (
                    <p className="text-center text-slate-500 py-4">Nessuna operazione registrata</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ENTRATE TAB */}
        <TabsContent value="entrate" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    Entrate
                  </CardTitle>
                  <CardDescription>Gestisci le entrate dell'associazione</CardDescription>
                </div>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input 
                      placeholder="Cerca..." 
                      className="pl-9 w-48" 
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <Button className="bg-green-600 hover:bg-green-700" onClick={() => { resetForm(); setShowEntrataDialog(true); }}>
                    <PlusCircle className="w-4 h-4 mr-2" />
                    Nuova Entrata
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 text-sm font-medium text-slate-600">Data</th>
                      <th className="text-left p-3 text-sm font-medium text-slate-600">Descrizione</th>
                      <th className="text-left p-3 text-sm font-medium text-slate-600">Categoria</th>
                      <th className="text-left p-3 text-sm font-medium text-slate-600">Modalità</th>
                      <th className="text-right p-3 text-sm font-medium text-slate-600">Importo</th>
                      <th className="text-center p-3 text-sm font-medium text-slate-600">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEntrate.map((item) => (
                      <tr key={item.entrata_id} className="border-b hover:bg-slate-50">
                        <td className="p-3 text-sm">{item.data_operazione}</td>
                        <td className="p-3 text-sm font-medium">{item.descrizione}</td>
                        <td className="p-3">
                          <Badge variant="outline" className="bg-green-50 text-green-700">
                            {item.categoria_nome}
                          </Badge>
                        </td>
                        <td className="p-3 text-sm capitalize">{item.modalita_incasso}</td>
                        <td className="p-3 text-right font-semibold text-green-600">
                          {formatCurrency(item.importo)}
                        </td>
                        <td className="p-3">
                          <div className="flex justify-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEditEntrata(item)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteEntrata(item.entrata_id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredEntrate.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    Nessuna entrata registrata
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* USCITE TAB */}
        <TabsContent value="uscite" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingDown className="w-5 h-5 text-red-600" />
                    Uscite
                  </CardTitle>
                  <CardDescription>Gestisci le uscite dell'associazione</CardDescription>
                </div>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input 
                      placeholder="Cerca..." 
                      className="pl-9 w-48" 
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <Button className="bg-red-600 hover:bg-red-700" onClick={() => { resetForm(); setShowUscitaDialog(true); }}>
                    <PlusCircle className="w-4 h-4 mr-2" />
                    Nuova Uscita
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 text-sm font-medium text-slate-600">Data</th>
                      <th className="text-left p-3 text-sm font-medium text-slate-600">Descrizione</th>
                      <th className="text-left p-3 text-sm font-medium text-slate-600">Categoria</th>
                      <th className="text-left p-3 text-sm font-medium text-slate-600">Modalità</th>
                      <th className="text-right p-3 text-sm font-medium text-slate-600">Importo</th>
                      <th className="text-center p-3 text-sm font-medium text-slate-600">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUscite.map((item) => (
                      <tr key={item.uscita_id} className="border-b hover:bg-slate-50">
                        <td className="p-3 text-sm">{item.data_operazione}</td>
                        <td className="p-3 text-sm font-medium">{item.descrizione}</td>
                        <td className="p-3">
                          <Badge variant="outline" className="bg-red-50 text-red-700">
                            {item.categoria_nome}
                          </Badge>
                        </td>
                        <td className="p-3 text-sm capitalize">{item.modalita_pagamento}</td>
                        <td className="p-3 text-right font-semibold text-red-600">
                          {formatCurrency(item.importo)}
                        </td>
                        <td className="p-3">
                          <div className="flex justify-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEditUscita(item)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteUscita(item.uscita_id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredUscite.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    Nessuna uscita registrata
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PROGETTI TAB */}
        <TabsContent value="progetti" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FolderKanban className="w-5 h-5 text-blue-600" />
                    Progetti
                  </CardTitle>
                  <CardDescription>Gestisci i progetti e monitora i budget</CardDescription>
                </div>
                <Button onClick={() => { setEditingItem(null); setProgettoForm({ nome: '', descrizione: '', data_inizio: '', data_fine: '', budget_previsto: '', stato: 'attivo' }); setShowProgettoDialog(true); }}>
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Nuovo Progetto
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {progetti.map((prog) => (
                  <Card key={prog.progetto_id} className="border">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-semibold text-slate-900">{prog.nome}</h3>
                          <Badge variant={prog.stato === 'attivo' ? 'default' : 'secondary'} className="mt-1">
                            {prog.stato}
                          </Badge>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditProgetto(prog)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteProgetto(prog.progetto_id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      {prog.descrizione && (
                        <p className="text-sm text-slate-500 mb-3 line-clamp-2">{prog.descrizione}</p>
                      )}
                      <div className="space-y-2 text-sm">
                        {prog.budget_previsto && (
                          <div className="flex justify-between">
                            <span className="text-slate-500">Budget:</span>
                            <span className="font-medium">{formatCurrency(prog.budget_previsto)}</span>
                          </div>
                        )}
                        {prog.data_inizio && (
                          <div className="flex justify-between">
                            <span className="text-slate-500">Inizio:</span>
                            <span>{prog.data_inizio}</span>
                          </div>
                        )}
                        {prog.data_fine && (
                          <div className="flex justify-between">
                            <span className="text-slate-500">Fine:</span>
                            <span>{prog.data_fine}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {progetti.length === 0 && (
                  <div className="md:col-span-3 text-center py-8 text-slate-500">
                    Nessun progetto creato
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* REPORT TAB */}
        <TabsContent value="report" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-purple-600" />
                    Report Economico
                  </CardTitle>
                  <CardDescription>Analisi delle entrate e uscite per anno</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={annoFiltro}
                    onChange={(e) => setAnnoFiltro(parseInt(e.target.value))}
                    className="border rounded-md px-3 py-2"
                  >
                    {[2024, 2025, 2026].map(anno => (
                      <option key={anno} value={anno}>{anno}</option>
                    ))}
                  </select>
                  <select
                    value={exportProgettoId}
                    onChange={(e) => setExportProgettoId(e.target.value)}
                    className="border rounded-md px-3 py-2"
                  >
                    <option value="">Tutti i progetti</option>
                    {progetti.map(prog => (
                      <option key={prog.progetto_id} value={prog.progetto_id}>{prog.nome}</option>
                    ))}
                  </select>
                  <Button
                    variant="outline"
                    onClick={() => handleExportCSV('completo', exportProgettoId || null)}
                    disabled={exporting}
                    className="border-green-500 text-green-700 hover:bg-green-50"
                  >
                    {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />}
                    CSV
                  </Button>
                  <Button
                    onClick={() => handleExportPDF(exportProgettoId || null)}
                    disabled={exporting}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                    PDF
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {report && (
                <div className="space-y-6">
                  {/* Totals */}
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-sm text-green-600">Totale Entrate {annoFiltro}</p>
                      <p className="text-2xl font-bold text-green-700">{formatCurrency(report.totale_entrate)}</p>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg">
                      <p className="text-sm text-red-600">Totale Uscite {annoFiltro}</p>
                      <p className="text-2xl font-bold text-red-700">{formatCurrency(report.totale_uscite)}</p>
                    </div>
                    <div className={`p-4 rounded-lg ${report.saldo >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
                      <p className={`text-sm ${report.saldo >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Saldo {annoFiltro}</p>
                      <p className={`text-2xl font-bold ${report.saldo >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                        {formatCurrency(report.saldo)}
                      </p>
                    </div>
                  </div>

                  {/* Categories breakdown */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Entrate per categoria */}
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Entrate per Categoria</h4>
                      <div className="space-y-2">
                        {report.entrate_per_categoria.map((cat, idx) => (
                          <div key={idx} className="flex justify-between items-center p-2 bg-green-50 rounded">
                            <span className="text-sm">{cat.categoria_nome}</span>
                            <span className="font-medium text-green-700">{formatCurrency(cat.totale)}</span>
                          </div>
                        ))}
                        {report.entrate_per_categoria.length === 0 && (
                          <p className="text-sm text-slate-500">Nessuna entrata nel periodo</p>
                        )}
                      </div>
                    </div>

                    {/* Uscite per categoria */}
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Uscite per Categoria</h4>
                      <div className="space-y-2">
                        {report.uscite_per_categoria.map((cat, idx) => (
                          <div key={idx} className="flex justify-between items-center p-2 bg-red-50 rounded">
                            <span className="text-sm">{cat.categoria_nome}</span>
                            <span className="font-medium text-red-700">{formatCurrency(cat.totale)}</span>
                          </div>
                        ))}
                        {report.uscite_per_categoria.length === 0 && (
                          <p className="text-sm text-slate-500">Nessuna uscita nel periodo</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Monthly breakdown */}
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3">Andamento Mensile</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2">Mese</th>
                            <th className="text-right p-2 text-green-600">Entrate</th>
                            <th className="text-right p-2 text-red-600">Uscite</th>
                            <th className="text-right p-2">Saldo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.entrate_per_mese.map((ent, idx) => {
                            const usc = report.uscite_per_mese[idx];
                            const saldo = ent.totale - usc.totale;
                            return (
                              <tr key={idx} className="border-b">
                                <td className="p-2">{ent.mese}</td>
                                <td className="p-2 text-right text-green-600">{formatCurrency(ent.totale)}</td>
                                <td className="p-2 text-right text-red-600">{formatCurrency(usc.totale)}</td>
                                <td className={`p-2 text-right font-medium ${saldo >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                                  {formatCurrency(saldo)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ENTRATA DIALOG */}
      <Dialog open={showEntrataDialog} onOpenChange={setShowEntrataDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Modifica Entrata' : 'Nuova Entrata'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Data *</label>
                <Input
                  type="date"
                  value={formData.data_operazione}
                  onChange={(e) => setFormData({...formData, data_operazione: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Importo (€) *</label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.importo}
                  onChange={(e) => setFormData({...formData, importo: e.target.value})}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Descrizione *</label>
              <Input
                placeholder="Es: Quota associativa 2025"
                value={formData.descrizione}
                onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Categoria *</label>
                <select
                  className="w-full border rounded-md px-3 py-2"
                  value={formData.categoria_id}
                  onChange={(e) => setFormData({...formData, categoria_id: e.target.value})}
                >
                  <option value="">Seleziona...</option>
                  {categorieEntrate.map(cat => (
                    <option key={cat.categoria_id} value={cat.categoria_id}>{cat.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Modalità Incasso</label>
                <select
                  className="w-full border rounded-md px-3 py-2"
                  value={formData.modalita_incasso}
                  onChange={(e) => setFormData({...formData, modalita_incasso: e.target.value})}
                >
                  <option value="banca">Banca</option>
                  <option value="posta">Posta</option>
                  <option value="contante">Contante</option>
                  <option value="carta">Carta</option>
                  <option value="paypal">PayPal</option>
                  <option value="altro">Altro</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Progetto (opzionale)</label>
              <select
                className="w-full border rounded-md px-3 py-2"
                value={formData.progetto_id}
                onChange={(e) => setFormData({...formData, progetto_id: e.target.value})}
              >
                <option value="">Nessun progetto</option>
                {progetti.map(prog => (
                  <option key={prog.progetto_id} value={prog.progetto_id}>{prog.nome}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Note</label>
              <Textarea
                placeholder="Note aggiuntive..."
                value={formData.note}
                onChange={(e) => setFormData({...formData, note: e.target.value})}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEntrataDialog(false)}>Annulla</Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={handleSaveEntrata}
              disabled={!formData.data_operazione || !formData.importo || !formData.descrizione || !formData.categoria_id}
            >
              {editingItem ? 'Salva Modifiche' : 'Registra Entrata'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* USCITA DIALOG */}
      <Dialog open={showUscitaDialog} onOpenChange={setShowUscitaDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Modifica Uscita' : 'Nuova Uscita'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Data *</label>
                <Input
                  type="date"
                  value={formData.data_operazione}
                  onChange={(e) => setFormData({...formData, data_operazione: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Importo (€) *</label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.importo}
                  onChange={(e) => setFormData({...formData, importo: e.target.value})}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Descrizione *</label>
              <Input
                placeholder="Es: Acquisto materiali"
                value={formData.descrizione}
                onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Categoria *</label>
                <select
                  className="w-full border rounded-md px-3 py-2"
                  value={formData.categoria_id}
                  onChange={(e) => setFormData({...formData, categoria_id: e.target.value})}
                >
                  <option value="">Seleziona...</option>
                  {categorieUscite.map(cat => (
                    <option key={cat.categoria_id} value={cat.categoria_id}>{cat.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Modalità Pagamento</label>
                <select
                  className="w-full border rounded-md px-3 py-2"
                  value={formData.modalita_pagamento}
                  onChange={(e) => setFormData({...formData, modalita_pagamento: e.target.value})}
                >
                  <option value="bonifico">Bonifico</option>
                  <option value="posta">Posta</option>
                  <option value="contante">Contante</option>
                  <option value="carta">Carta</option>
                  <option value="paypal">PayPal</option>
                  <option value="altro">Altro</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Progetto (opzionale)</label>
              <select
                className="w-full border rounded-md px-3 py-2"
                value={formData.progetto_id}
                onChange={(e) => setFormData({...formData, progetto_id: e.target.value})}
              >
                <option value="">Nessun progetto</option>
                {progetti.map(prog => (
                  <option key={prog.progetto_id} value={prog.progetto_id}>{prog.nome}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Note</label>
              <Textarea
                placeholder="Note aggiuntive..."
                value={formData.note}
                onChange={(e) => setFormData({...formData, note: e.target.value})}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUscitaDialog(false)}>Annulla</Button>
            <Button 
              className="bg-red-600 hover:bg-red-700"
              onClick={handleSaveUscita}
              disabled={!formData.data_operazione || !formData.importo || !formData.descrizione || !formData.categoria_id}
            >
              {editingItem ? 'Salva Modifiche' : 'Registra Uscita'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PROGETTO DIALOG */}
      <Dialog open={showProgettoDialog} onOpenChange={setShowProgettoDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Modifica Progetto' : 'Nuovo Progetto'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Nome Progetto *</label>
              <Input
                placeholder="Es: Raccolta alimentare 2025"
                value={progettoForm.nome}
                onChange={(e) => setProgettoForm({...progettoForm, nome: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Descrizione</label>
              <Textarea
                placeholder="Descrizione del progetto..."
                value={progettoForm.descrizione}
                onChange={(e) => setProgettoForm({...progettoForm, descrizione: e.target.value})}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Data Inizio</label>
                <Input
                  type="date"
                  value={progettoForm.data_inizio}
                  onChange={(e) => setProgettoForm({...progettoForm, data_inizio: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Data Fine</label>
                <Input
                  type="date"
                  value={progettoForm.data_fine}
                  onChange={(e) => setProgettoForm({...progettoForm, data_fine: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Budget Previsto (€)</label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={progettoForm.budget_previsto}
                  onChange={(e) => setProgettoForm({...progettoForm, budget_previsto: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Stato</label>
                <select
                  className="w-full border rounded-md px-3 py-2"
                  value={progettoForm.stato}
                  onChange={(e) => setProgettoForm({...progettoForm, stato: e.target.value})}
                >
                  <option value="attivo">Attivo</option>
                  <option value="concluso">Concluso</option>
                  <option value="sospeso">Sospeso</option>
                </select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProgettoDialog(false)}>Annulla</Button>
            <Button 
              onClick={handleSaveProgetto}
              disabled={!progettoForm.nome}
            >
              {editingItem ? 'Salva Modifiche' : 'Crea Progetto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
